// src/features/devices/history/DeviceHistoryPanel.tsx
import React, { useMemo } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { FaTrash, FaHistory, FaUndo, FaRedo } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  IDeviceUpdateCommand,
  selectCanUndo,
  selectCanRedo,
  selectDeviceHistory,
  clearHistoryInRedux,
} from "./deviceHistorySlice";
import { thunkRedo } from "./thunkRedo";
import { thunkUndo } from "./thunkUndo";

const formatCm = (cm: number): string => {
  const rounded = Math.round(cm);
  return `${rounded} cm`;
};

const formatSignedCm = (cm: number): string => {
  const sign = cm >= 0 ? "+" : "−";
  return `${sign}${formatCm(Math.abs(cm))}`;
};

const formatTiles = (tiles: number): string => {
  // Vis uten desimal når det er "nesten" et helt tall, ellers 1 desimal
  const rounded = Math.round(tiles);
  const isNearlyInt = Math.abs(tiles - rounded) < 0.05;
  return isNearlyInt ? `${rounded} plattor` : `${tiles.toFixed(1)} plattor`;
};

const getMoveInfo = (before?: number[], after?: number[]) => {
  if (!before || !after || before.length < 2 || after.length < 2) return null;

  // Coordinate er i meter (som du konkluderte med)
  const dxM = after[0] - before[0];
  const dyM = after[1] - before[1];
  const distM = Math.sqrt(dxM * dxM + dyM * dyM);

  const dxCm = dxM * 100;
  const dyCm = dyM * 100;
  const distCm = distM * 100;

  const tiles = distM / 0.6; // 600x600 mm = 0.6 m

  return { dxCm, dyCm, distCm, tiles };
};

const buildMoveSummary = (cmd: IDeviceUpdateCommand): { title: string; detail?: string } => {
  const info = getMoveInfo(cmd.before.coordinate, cmd.after.coordinate);

  if (!info) return { title: "Oppdaterte komponenter" };

  // Hvis distansen er ekstremt liten, kan vi behandle det som "ingen flytt"
  if (Math.abs(info.distCm) < 0.5) {
    return { title: "Oppdaterte komponenter" };
  }

  const title = `Flyttet: ${formatCm(info.distCm)} (${formatTiles(info.tiles)})`;
  const detail = `Δx ${formatSignedCm(info.dxCm)}, Δy ${formatSignedCm(info.dyCm)}`;

  return { title, detail };
};

export const DeviceHistoryPanel: React.FC = () => {
  const dispatch = useAppDispatch();

  const canUndo = useAppSelector(selectCanUndo);
  const canRedo = useAppSelector(selectCanRedo);
  const history = useAppSelector(selectDeviceHistory);

  const totalCount = history.undoStack.length + history.redoStack.length;

  return (
    <Paper
      shadow="md"
      radius="md"
      withBorder
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Fast header */}
      <Group justify="space-between" px="sm" py="xs" style={{ flexShrink: 0 }}>
        <Group gap="xs">
          <FaHistory size={16} />
          <Text fw={600}>Historikk</Text>
          <Badge variant="light">Undo: {history.undoStack.length}</Badge>
          <Badge variant="light">Redo: {history.redoStack.length}</Badge>
        </Group>

        <Group gap="xs">
          <Tooltip label="Angre (Ctrl/Cmd+Z)" withArrow>
            <Button
              size="xs"
              leftSection={<FaUndo size={14} />}
              disabled={!canUndo}
              onClick={() => dispatch(thunkUndo())}
              variant="light"
            >
              Angre
            </Button>
          </Tooltip>

          <Tooltip label="Gjør om (Ctrl+Y / Cmd+Shift+Z)" withArrow>
            <Button
              size="xs"
              leftSection={<FaRedo size={14} />}
              disabled={!canRedo}
              onClick={() => dispatch(thunkRedo())}
              variant="light"
            >
              Gjør om
            </Button>
          </Tooltip>

          <Tooltip label="Tøm historikk" withArrow>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => dispatch(clearHistoryInRedux())}
              aria-label="Tøm historikk"
            >
              <FaTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Divider />

      {/* Scrollbart område – tar resten av höjden */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          maxHeight: "calc(100dvh - 300px)",
          willChange: "scroll-position",
          paddingBottom: "1px",
        }}
      >
        <Stack gap="xs" p="sm" style={{ minHeight: "100%" }}>
          {totalCount === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="xl">
              Ingen endringer enda.
            </Text>
          ) : (
            <>
              <Text size="xs" c="dimmed" ta="center">
                Øverst = nyest. Undo-listen viser handlinger du kan angre. Redo-listen viser
                handlinger du kan gjøre om.
              </Text>

              <Divider label="Undo" labelPosition="left" />

              {[...history.undoStack].reverse().map((cmd, idx) => (
                <HistoryCard key={`undo-${idx}-${cmd.deviceId}`} side="UNDO" cmd={cmd} />
              ))}

              {history.redoStack.length > 0 && (
                <>
                  <Divider label="Redo" labelPosition="left" mt="md" />

                  {[...history.redoStack].reverse().map((cmd, idx) => (
                    <HistoryCard key={`redo-${idx}-${cmd.deviceId}`} side="REDO" cmd={cmd} />
                  ))}
                </>
              )}
            </>
          )}
        </Stack>
      </div>
    </Paper>
  );
};

const HistoryCard: React.FC<{ side: "UNDO" | "REDO"; cmd: IDeviceUpdateCommand }> = ({
  side,
  cmd,
}) => {
  const { title, detail } = useMemo(() => buildMoveSummary(cmd), [cmd]);
  const ts = cmd.meta?.createdAtIso ?? null;

  const compCode = cmd.after.compCode ?? cmd.before.compCode ?? "Ukjent compCode";

  return (
    <Paper withBorder radius="md" p="sm">
      <Stack gap={6}>
        <Group gap="xs">
          <Badge variant="light" color={side === "UNDO" ? "blue" : "grape"}>
            {side === "UNDO" ? "Angrebar" : "Gjørbar"}
          </Badge>
          <Badge variant="outline">{cmd.meta?.reason ?? "OTHER"}</Badge>
        </Group>

        <Text size="sm" fw={700} lineClamp={2}>
          {title}
        </Text>

        {detail ? (
          <Text size="xs" c="dimmed">
            {detail}
          </Text>
        ) : null}

        <Text size="xs" c="dimmed">
          Komponent: {compCode}
          {cmd.meta?.roomNumber ? ` · Rom: ${cmd.meta.roomNumber}` : ""}
          {cmd.meta?.projectNo ? ` · Prosjekt: ${cmd.meta.projectNo}` : ""}
        </Text>

        {ts ? (
          <Text size="xs" c="dimmed">
            Tid: {new Date(ts).toLocaleString()}
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
};
